import { useState, useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { TablePaginationConfig } from 'antd'
import fetchAPI, { IHttpError } from '../../../../lib/utils/fetch-api'
import { objectToUrl } from '../../utils'
import { Task, Sorter, ApiResponse, IError } from '../types'
import { ResultStatusType } from 'antd/lib/result'

export const useTasksData = () => {
  const location = useLocation()
  const isFirstLoad = useRef(true)

  const [data, setData] = useState<Task[]>([])
  const [pageLoading, setPageLoading] = useState<boolean>(true) // Для первичной загрузки
  const [dataLoading, setDataLoading] = useState<boolean>(false) // Для обновлений таблицы
  const [pageError, setPageError] = useState<null | IError>(null) // Ошибка первичной загрузки

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 10,
    position: ['bottomCenter'],
    showSizeChanger: false,
  })
  const [sorter, setSorter] = useState<Sorter>({})
  const [options, setOptions] = useState<string[]>([])

  const updatePagination = useCallback((newState: Partial<TablePaginationConfig>) => {
    setPagination(prev => ({
      ...prev,
      ...newState,
    }))
  }, [])

  const getData = useCallback(async () => {
    // Сохраняем состояние первой загрузки ДО начала работы
    const isFirstLoadNow = isFirstLoad.current

    // Если первая загрузка - pageLoading, иначе - dataLoading
    if (isFirstLoadNow) {
      setPageLoading(true)
      setPageError(null)
    } else {
      setDataLoading(true)
    }

    // Всегда очищаем данные для корректного обновления
    //setData([])

    const currentUrlParams = Object.fromEntries(new URLSearchParams(location.search))

    try {
      const response = ((await fetchAPI(
        `/api/v2/tasks${objectToUrl(currentUrlParams)}`,
      )) as unknown) as ApiResponse
      const { data, pagination, sorter, options } = response

      updatePagination(pagination)
      setSorter(sorter)
      setData(data)
      setOptions(options)
    } catch (e) {
      const error = {
        message: (e as IHttpError)?.message || 'Неизвестная ошибка',
        code: ((e as IHttpError)?.statusCode || 'error') as ResultStatusType,
      }

      // Ошибка на первой загрузке - критичная
      if (isFirstLoadNow) {
        setPageError(error)
      } else {
        // Ошибка при обновлении - можно залогировать или показать notification
        console.error('Ошибка загрузки данных:', error)
      }
    } finally {
      // Используем сохраненное значение
      if (isFirstLoadNow) {
        setPageLoading(false)
        isFirstLoad.current = false // Сбрасываем флаг после завершения
      } else {
        setDataLoading(false)
      }
    }
  }, [location.search, updatePagination])

  // Загружаем данные при монтировании и изменении URL
  useEffect(() => {
    getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]) // Зависим напрямую от location.search для предотвращения дублирующихся вызовов

  const remove = useCallback(
    (id: number) => {
      fetchAPI(`/api/v2/task/${id}`, { method: 'DELETE' })
        .then(() => {
          getData()
        })
        .catch(e => {
          console.error({ e })
        })
    },
    [getData],
  )

  return {
    data,
    pageLoading, // Для Skeleton при первой загрузке
    dataLoading, // Для loading таблицы при обновлениях
    pageError, // Критичная ошибка первой загрузки
    pagination,
    sorter,
    options,
    getData,
    remove,
    setSorter,
    updatePagination,
  }
}
